import {DateTime} from '@webmate-web-core/shared/types/base-types';
import {FeatureId} from '@webmate-web-core/shared/types/feature';
import {
    CustomerId,
    MarketplaceActionId,
    MarketplaceCatalogOfferingRef,
    MarketplaceContentBlockId,
    MarketplaceInteractionRecordId,
    MarketplaceItemId,
    MarketplaceServiceRef,
    MarketplaceTagKey,
    ProjectId,
    UserId
} from '@webmate-web-core/shared/types/ids';

export const MarketplaceFeatureId: FeatureId = 'webmate.marketplace';
export const MarketplacePricingFeatureId: FeatureId = 'webmate.marketplace.pricing';

export interface MarketplaceTagDefinition {
    key: MarketplaceTagKey;
    label: string;
    rank: number;
    featured: boolean;
}

export type MarketplacePresentationKind = 'capability' | 'solution' | 'service' | 'roadmapIdea';
export type MarketplacePublicationState = 'draft' | 'published' | 'retired';
export type MarketplaceGrantScope = 'user' | 'project' | 'organization';
export type MarketplacePlacementSlot = 'home.hero' | 'home.recommended' | 'home.services' | 'home.roadmap';
export type MarketplaceStatusStyle = 'available' | 'requires-approval' | 'in-exploration' | 'coming-soon';
export type MarketplaceKindStyle = 'capability' | 'solution' | 'service' | 'roadmap-idea';

export interface MarketplaceMetric {
    value: string;
    label: string;
}

export interface MarketplacePart {
    title: string;
    description: string;
    kind: 'included' | 'optional';
}

export interface MarketplaceImage {
    src: string;
    alt: string;
    caption?: string;
    decorative?: boolean;
}

interface MarketplaceContentBlockBase {
    id: MarketplaceContentBlockId;
    heading?: string;
}

export interface MarketplaceRichTextBlock extends MarketplaceContentBlockBase {
    kind: 'richText';
    paragraphs: string[];
}

export interface MarketplaceMetricsBlock extends MarketplaceContentBlockBase {
    kind: 'metrics';
    items: MarketplaceMetric[];
}

export interface MarketplaceFeatureListBlock extends MarketplaceContentBlockBase {
    kind: 'featureList';
    items: string[];
}

export interface MarketplacePartsBlock extends MarketplaceContentBlockBase {
    kind: 'parts';
    items: MarketplacePart[];
}

export interface MarketplaceCalloutBlock extends MarketplaceContentBlockBase {
    kind: 'callout';
    tone: 'info' | 'tip' | 'warning';
    body: string;
}

export interface MarketplaceImageBlock extends MarketplaceContentBlockBase {
    kind: 'image';
    image: MarketplaceImage;
    width?: 'content' | 'wide';
}

export interface MarketplaceScreenshotGalleryBlock extends MarketplaceContentBlockBase {
    kind: 'screenshotGallery';
    intro?: string;
    images: MarketplaceImage[];
}

export interface MarketplaceResourceLink {
    title: string;
    url: string;
    description?: string;
    source?: string;
}

export interface MarketplaceResourceLinksBlock extends MarketplaceContentBlockBase {
    kind: 'resourceLinks';
    intro?: string;
    links: MarketplaceResourceLink[];
}

export interface MarketplaceStepsBlock extends MarketplaceContentBlockBase {
    kind: 'steps';
    intro?: string;
    items: string[];
}

export interface MarketplaceBulletListBlock extends MarketplaceContentBlockBase {
    kind: 'bulletList';
    intro?: string;
    items: string[];
}

export interface MarketplaceMaturityBlock extends MarketplaceContentBlockBase {
    kind: 'maturity';
    steps: string[];
    activeStep: string;
}

export interface MarketplaceIllustrationBlock extends MarketplaceContentBlockBase {
    kind: 'illustration';
    description?: string;
    visual: 'loadProfile';
}

export type MarketplaceContentBlock = MarketplaceRichTextBlock
    | MarketplaceMetricsBlock
    | MarketplaceFeatureListBlock
    | MarketplacePartsBlock
    | MarketplaceCalloutBlock
    | MarketplaceImageBlock
    | MarketplaceScreenshotGalleryBlock
    | MarketplaceResourceLinksBlock
    | MarketplaceStepsBlock
    | MarketplaceBulletListBlock
    | MarketplaceMaturityBlock
    | MarketplaceIllustrationBlock;

export interface MarketplaceFeaturedPresentation {
    metric?: MarketplaceMetric;
    visual?: 'loadProfile';
    commercialQualifier?: string;
}

export interface MarketplaceRoadmapState {
    status: 'inExploration' | 'comingSoon';
}

interface MarketplaceActionBase {
    id: MarketplaceActionId;
    label: string;
    primary: boolean;
}

export interface MarketplaceAcquireAction extends MarketplaceActionBase {
    kind: 'acquire';
    catalogOfferingRefs: MarketplaceCatalogOfferingRef[];
    available: boolean;
    requiresApproval: boolean;
    grantScopes: MarketplaceGrantScope[];
    commercialSummary: string;
    commercialNote: string;
}

export interface MarketplaceRequestServiceAction extends MarketplaceActionBase {
    kind: 'requestService';
    serviceRef?: MarketplaceServiceRef;
    commercialSummary?: string;
    commercialNote?: string;
}

export interface MarketplaceSignalInterestAction extends MarketplaceActionBase {
    kind: 'signalInterest';
}

export interface MarketplaceProvideFeedbackAction extends MarketplaceActionBase {
    kind: 'provideFeedback';
}

export type MarketplaceAction = MarketplaceAcquireAction
    | MarketplaceRequestServiceAction
    | MarketplaceSignalInterestAction
    | MarketplaceProvideFeedbackAction;

export interface MarketplaceItemPlacement {
    slot: MarketplacePlacementSlot;
    rank: number;
}

/**
 * Published Marketplace item returned by the backend. It combines editorial
 * content with the catalog-derived fields needed for presentation.
 */
export interface MarketplaceItem {
    id: MarketplaceItemId;
    presentationKind: MarketplacePresentationKind;
    publicationState: MarketplacePublicationState;
    tagKeys: MarketplaceTagKey[];
    title: string;
    teaser: string;
    valueProp: string;
    provider: string;
    body: MarketplaceContentBlock[];
    featuredPresentation?: MarketplaceFeaturedPresentation;
    interestCount?: number;
    relatedItemIds: MarketplaceItemId[];
    actions: MarketplaceAction[];
    roadmapState?: MarketplaceRoadmapState;
    tags: MarketplaceTagDefinition[];
    primaryAction: MarketplaceAction;
    kindLabel: string;
    kindStyle: MarketplaceKindStyle;
    statusLabel: string;
    statusStyle: MarketplaceStatusStyle;
    placements: MarketplaceItemPlacement[];
    commercialSummary?: string;
    commercialNote?: string;
}

export type MarketplaceGrantTarget = {
    type: 'user';
    userId: UserId;
} | {
    type: 'project';
    projectId: ProjectId;
} | {
    type: 'projects';
    projectIds: ProjectId[];
} | {
    type: 'allProjects';
} | {
    type: 'organization';
    organizationId: CustomerId;
};

/** Read projection of a persisted acquisition interaction. */
export interface MarketplaceRequest {
    id: MarketplaceInteractionRecordId;
    recordedAt: DateTime;
    requesterUserId: UserId;
    customerId: CustomerId;
    marketplaceItemId: MarketplaceItemId;
    itemTitleSnapshot: string;
    actionId: MarketplaceActionId;
    catalogOfferingRefs: MarketplaceCatalogOfferingRef[];
    target: MarketplaceGrantTarget;
    note?: string | null;
}

interface MarketplaceInteractionSubmissionBase {
    marketplaceItemId: MarketplaceItemId;
    itemTitleSnapshot: string;
    /**
     * Transport name for the Marketplace action identifier. The backend maps
     * this field to its domain-owned MarketplaceActionId.
     */
    interactionId: MarketplaceActionId;
}

export type MarketplaceInteractionSubmission = MarketplaceInteractionSubmissionBase & ({
    payload: {
        type: 'acquisitionRequest';
        catalogOfferingRefs: MarketplaceCatalogOfferingRef[];
        target: MarketplaceGrantTarget;
        note?: string;
    };
} | {
    payload: {
        type: 'serviceInquiry';
        serviceRef?: MarketplaceServiceRef;
        message: string;
    };
} | {
    payload: {
        type: 'interestChanged';
        interested: boolean;
    };
} | {
    payload: {
        type: 'feedback';
        message: string;
    };
});

export interface MarketplaceInteractionRecorded {
    id: MarketplaceInteractionRecordId;
    recordedAt: DateTime;
}
